import { useCallback, useEffect, useState } from 'react';

import { useGetCollaboratorEmails } from '@/hooks/useAccounts';
import { useAxios } from '@/hooks/useAxios';
import { useMy } from '@/hooks/useMy';
import { useAuth } from '@/state/auth';
import { Account, Setting, Statistics } from '@/types';
import { QueryStatus } from '@/utils/api';

interface Collaborator {
  email: string;
  displayName: string;
  counts?: Statistics;
  updated?: string;
}

interface CollaboratorProps {
  getRiskCounts?: boolean;
  doNotImpersonate?: boolean;
  enabled?: boolean;
  email?: string;
}

export function useGetCollaborators(props?: CollaboratorProps) {
  const { getToken, me, friend } = useAuth();

  const axios = useAxios();

  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [collaboratorsStatus, setCollaboratorsStatus] =
    useState<QueryStatus>('pending');

  const { data: accounts, status: accountsStatus } = useMy(
    {
      resource: 'account',
    },
    {
      doNotImpersonate: props?.doNotImpersonate,
      email: props?.email,
      enabled: props?.enabled ?? true,
    }
  );

  const primaryEmail = props?.doNotImpersonate ? me : friend;

  const collaboratorEmails = useGetCollaboratorEmails(accounts);

  const fetchCollaboratorsData = useCallback(
    async (emails: string[]) => {
      setCollaboratorsStatus('pending');
      const token = await getToken();

      const requests = emails.map(async email => {
        const headers = {
          Authorization: token ? `Bearer ${token}` : '',
          account: email,
        };

        try {
          const [settingsResponse, accountsResponse, counts] =
            await Promise.all([
              axios<{ settings: Setting[] }>({
                method: 'get',
                url: `/my?key=${encodeURIComponent('#setting')}`,
                headers,
              }).then(response => response.data.settings),
              axios<{ accounts: Account[] }>({
                method: 'get',
                url: `/my?key=${encodeURIComponent('#account')}`,
                headers,
              }).then(response => response.data.accounts),
              props?.getRiskCounts
                ? axios<Statistics>({
                    method: 'get',
                    url: '/my',
                    params: { key: '#statistic#my#status:O', desc: true },
                    headers,
                  }).then(response => response.data)
                : Promise.resolve(undefined),
            ]);

          // Extract display name from settings
          const displayNameSetting = settingsResponse?.find(
            s => s.name === 'display-name'
          );
          const displayName =
            typeof displayNameSetting?.value === 'string'
              ? displayNameSetting.value
              : '';

          return {
            displayName,
            email,
            counts,
            updated: accountsResponse.find(
              ({ member }) => member === primaryEmail
            )?.updated,
          };
        } catch (error) {
          return error;
        }
      });

      const results = await Promise.all(requests);

      const validResults = results.filter(
        (r): r is Collaborator => !(r instanceof Error)
      );

      setCollaborators(validResults);
      setCollaboratorsStatus('success');
    },
    [axios, props?.getRiskCounts]
  );

  useEffect(() => {
    if (accountsStatus === 'success') {
      if (collaboratorEmails.length > 0) {
        fetchCollaboratorsData(collaboratorEmails);
      } else {
        setCollaborators([]);
        setCollaboratorsStatus('success');
      }
    } else if (accountsStatus === 'error') {
      setCollaboratorsStatus('error');
    }
  }, [collaboratorEmails, fetchCollaboratorsData, accountsStatus]);

  return {
    data: collaborators,
    status: collaboratorsStatus,
    refresh: () => {
      fetchCollaboratorsData(collaboratorEmails);
    },
  };
}
